import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaGlobe, FaPhone, FaEnvelope, FaArrowLeft, FaArrowRight, FaStar, FaRegStar,
  FaUsers, FaCalendarAlt, FaShareAlt, FaPaperPlane, FaBuilding,
  FaFacebook, FaTwitter, FaLinkedin, FaInstagram, FaYoutube
} from 'react-icons/fa';
import { 
  getFirestore, doc, getDoc, collection, query, 
  where, orderBy, getDocs, addDoc, serverTimestamp 
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Spinner from './Spinner';
import Header from './Header';
import Footer from './Footer';
import defaultLogo from '../assets/default-org-logo.svg';
import SignupModal from './SignupModal';
import ShareOrganizationLink from './ShareOrganizationLink';
import JobCard from './jobs/JobCard';

const OrganizationPublicView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(5);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [hasUserReviewed, setHasUserReviewed] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  
  const db = getFirestore();
  const auth = getAuth();

  // Fetch organization data
  const fetchOrganization = async () => {
    try {
      const orgRef = doc(db, 'organizations', id);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists()) {
        console.log('No organization found with ID:', id);
        return false;
      }
      
      const orgData = { id: orgDoc.id, ...orgDoc.data() };
      setOrg(orgData);
      return true;
    } catch (error) {
      console.error('Error fetching organization:', error);
      return false;
    }
  };

  // Format date to relative time
  const formatDate = (date) => {
    if (!date || !date.toDate) return '';
    const now = new Date();
    const diffInSeconds = Math.floor((now - date.toDate()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toDate().toLocaleDateString();
  };

  // Fetch reviews
  const fetchReviews = async () => {
    try {
      const reviewsQuery = query(
        collection(db, 'reviews'),
        where('organizationId', '==', id),
        orderBy('createdAt', 'desc')
      );
      const reviewsSnapshot = await getDocs(reviewsQuery);
      setReviews(reviewsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  // Handle review submission
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setShowSignupModal(true);
      return;
    }

    if (currentUser.uid === id) {
      toast.error("You cannot review your own organization.");
      return;
    }

    const reviewsQuery = query(
      collection(db, 'reviews'),
      where('organizationId', '==', id),
      where('userId', '==', currentUser.uid)
    );
    const existingReviewsSnapshot = await getDocs(reviewsQuery);

    if (!existingReviewsSnapshot.empty) {
      toast.error('You have already submitted a review for this organization.');
      return;
    }

    if (!reviewText.trim()) {
      toast.error('Please enter your review');
      return;
    }

    try {
      await addDoc(collection(db, 'reviews'), {
        organizationId: id,
        userId: currentUser.uid,
        userName: currentUser.displayName || 'Anonymous',
        userPhoto: currentUser.photoURL,
        rating,
        text: reviewText,
        createdAt: serverTimestamp(),
      });
      setReviewText('');
      setRating(5);
      toast.success('Review submitted successfully!');
      fetchReviews(); // Refresh reviews
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    }
  };

  // Fetch jobs posted by this organization
  const fetchJobs = async (orgId) => {
    try {
      setLoadingJobs(true);
      const jobsRef = collection(db, 'jobs');
      const q = query(
        jobsRef,
        where('organizationId', '==', orgId),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const jobsList = [];
      
      querySnapshot.forEach((doc) => {
        jobsList.push({ id: doc.id, ...doc.data() });
      });
      
      setJobs(jobsList);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const orgExists = await fetchOrganization();
        if (orgExists) {
          await fetchReviews();
          await fetchJobs(id);
        }
      } catch (error) {
        console.error('Error loading page data:', error);
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setIsAuthenticated(!!user);
      setCurrentUser(user);
      if (user) {
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('organizationId', '==', id),
          where('userId', '==', user.uid)
        );
        const existingReviewsSnapshot = await getDocs(reviewsQuery);
        setHasUserReviewed(!existingReviewsSnapshot.empty);
      } else {
        setHasUserReviewed(false);
      }
    });

    fetchData();
    return () => unsubscribe();
  }, [id, db]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center p-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Organization Not Found</h2>
            <button 
              onClick={() => navigate(-1)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center mx-auto"
            >
              <FaArrowLeft className="mr-2" /> Go Back
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length).toFixed(1)
    : 0;

  const socialIcons = {
    facebook: <FaFacebook />,
    twitter: <FaTwitter />,
    linkedin: <FaLinkedin />,
    instagram: <FaInstagram />,
    youtube: <FaYoutube />,
  };


  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      {/* Profile Header */}
      <div className="relative bg-white rounded-t-xl shadow-sm">
        <div className="relative h-48 bg-gradient-to-r from-indigo-900 via-purple-800 to-indigo-900 overflow-hidden">
          {org.bannerUrl ? (
            <img src={org.bannerUrl} alt="Banner" className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-indigo-800"></div>
          )}
          <div className="absolute top-4 right-4 z-10 bg-white/80 px-3 py-1 rounded-full shadow text-xs font-semibold text-gray-700">
            Powered by <span className="font-bold">asdflo</span>
          </div>
        </div>
        <div className="relative px-6 md:px-8 pb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end -mt-16 relative z-10">
            <div className="flex flex-col items-start">
              <div className="relative group mb-3">
                <div className="w-32 h-32 rounded-lg bg-white border-4 border-white shadow-xl overflow-hidden">
                  {org.logoUrl ? (
                    <img src={org.logoUrl} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <FaBuilding className="w-16 h-16 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
              <div className="w-full">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {org.orgName || 'Organization Name'}
                  </h1>
                  <ShareOrganizationLink organizationId={id} />
                </div>
                {org.description && <p className="mt-1 text-gray-600">{org.description}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 sm:mt-0">
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Left/Main Column */}
            <div className="lg:col-span-2 space-y-8">
              {/* About Section */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">About Us</h2>
                <div className="prose max-w-none text-gray-700 whitespace-pre-wrap">{org.aboutUs || 'No information provided.'}</div>
              </div>

              {/* Impact Section */}
              {org.impact && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Impact</h2>
                  <div className="prose max-w-none text-gray-700 whitespace-pre-wrap">{org.impact}</div>
                </div>
              )}

              {/* Services Section */}
              {org.services && org.services.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Services</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {org.services.map((service, i) => (
                      <div key={i} className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-gray-900">{service.name}</h3>
                        {service.description && <p className="mt-1 text-sm text-gray-600">{service.description}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Jobs Section */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-800">Open Positions</h2>
                  {jobs.length > 0 && (
                    <Link 
                      to={`/jobs?organization=${id}`} 
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                    >
                      View all jobs <FaArrowRight className="ml-1" />
                    </Link>
                  )}
                </div>
                
                {loadingJobs ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="md" />
                  </div>
                ) : jobs.length > 0 ? (
                  <div className="space-y-4">
                    {jobs.slice(0, 3).map((job) => (
                      <div key={job.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <JobCard 
                          job={job} 
                          showSaveButton={false}
                          onViewDetails={(jobId) => navigate(`/jobs/${jobId}`)}
                        />
                      </div>
                    ))}
                    {jobs.length > 3 && (
                      <div className="text-center mt-4">
                        <Link 
                          to={`/jobs?organization=${id}`}
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View all {jobs.length} positions <FaArrowRight className="ml-1" />
                        </Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No open positions at the moment. Check back later!
                  </div>
                )}
              </div>

              {/* Reviews Section */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-800">Reviews</h2>
                  {reviews.length > 0 && (
                    <div className="flex items-center">
                      <span className="text-yellow-400 flex items-center">
                        {[...Array(5)].map((_, i) => i < Math.round(avgRating) ? <FaStar key={i} /> : <FaRegStar key={i} />)}
                      </span>
                      <span className="ml-2 font-bold text-gray-700">{avgRating}</span>
                      <span className="ml-1 text-gray-500">({reviews.length})</span>
                    </div>
                  )}
                </div>

                {/* Review Submission Form */}
                {(!isAuthenticated || (currentUser?.uid !== id && !hasUserReviewed)) && (
                  <form onSubmit={handleReviewSubmit} className="mb-6 p-4 border rounded-lg bg-gray-50">
                    <h3 className="font-semibold mb-2">Leave a Review</h3>
                    <div className="flex items-center mb-2">
                      {[...Array(5)].map((_, i) => (
                        <FaStar 
                          key={i} 
                          className={`cursor-pointer ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
                          onClick={() => setRating(i + 1)}
                        />
                      ))}
                    </div>
                    <textarea 
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Share your experience..."
                      className="w-full p-2 border rounded-md mb-2"
                      rows="3"
                    ></textarea>
                    <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center">
                      <FaPaperPlane className="mr-2" /> Submit Review
                    </button>
                  </form>
                )}

                {isAuthenticated && currentUser?.uid !== id && hasUserReviewed && (
                  <div className="mb-6 p-4 border rounded-lg bg-gray-100 text-center">
                    <p className="font-semibold text-gray-700">You have already reviewed this organization.</p>
                  </div>
                )}

                {/* Reviews List */}
                <div className="space-y-6">
                  {reviews.length > 0 ? (
                    reviews.map(review => (
                      <div key={review.id} className="flex items-start gap-4 border-t pt-6 first:border-t-0 first:pt-0">
                        <img src={review.userPhoto || defaultLogo} alt={review.userName} className="w-12 h-12 rounded-full" />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold">{review.userName}</p>
                            <span className="text-sm text-gray-500">{formatDate(review.createdAt)}</span>
                          </div>
                          <div className="text-yellow-400 flex items-center my-1">
                            {[...Array(5)].map((_, i) => i < review.rating ? <FaStar key={i} /> : <FaRegStar key={i} />)}
                          </div>
                          <p className="text-gray-700">{review.text}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 italic">No reviews yet. Be the first to share your experience!</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right/Sidebar Column */}
            <div className="lg:col-span-1 space-y-8" id="contact">
              {/* Contact Card */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Contact Information</h2>
                {isAuthenticated ? (
                  <div className="space-y-4">
                    {org.contactEmail && (
                      <div className="flex items-start">
                        <FaEnvelope className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Email</p>
                          <a href={`mailto:${org.contactEmail}`} className="text-blue-600 hover:underline">{org.contactEmail}</a>
                        </div>
                      </div>
                    )}
                    {org.contactPhone && (
                      <div className="flex items-start">
                        <FaPhone className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Phone</p>
                          <a href={`tel:${org.contactPhone}`} className="text-gray-900 hover:underline">{org.contactPhone}</a>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-600 mb-3">Please log in to view contact details.</p>
                    <button onClick={() => setShowSignupModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                      Log In / Sign Up
                    </button>
                  </div>
                )}
                {(org.city || org.state || org.country) && (
                  <div className="flex items-start pt-4 mt-4 border-t">
                    <FaMapMarkerAlt className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="text-gray-900">
                        {[org.city, org.state, org.country].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Social Media Card */}
              {org.socialMedia && Object.values(org.socialMedia).some(v => v) && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Follow Us</h2>
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(org.socialMedia).map(([platform, url]) => url && (
                      <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-600 text-2xl">
                        {socialIcons[platform]}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </main>
      
      <Footer />
      <SignupModal show={showSignupModal} onClose={() => setShowSignupModal(false)} />
    </div>
  );
};

export default OrganizationPublicView;
